import first from 'lodash/first'
import React, { createRef, FC, useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import { Modal } from '@Components/Modal'
import { DialogBody, DialogWrapper } from '@Components/Modal/Dialog/styledElements'
import { PersonalisationModal } from '@Components/Personalization/Modals/Shared'
import UVPrintStepTwo from '@Components/Personalization/Modals/UVPrint/UVPrintStepTwo'
import {
  getPersonalizationsByType,
  getPersonalizationsFromProduct,
} from '@Globals/CMS/ProductHelpers'
import { up } from '@Globals/Style/breakpoints'

import UVPrintStepOne from './UVPrintStepOne'

export interface UVPrintState {
  close: boolean
  imageState: ImagePrintState | null
  nextStep: () => void
  step: number
  updateProduct?: boolean
}

export interface ImagePrintState {
  image: File | Blob | Blob[] | undefined
  name: string
  ready: boolean
  type: string
}

const initState: UVPrintState = {
  imageState: {
    image: undefined,
    name: '',
    ready: false,
    type: '',
  },
  step: 1,
  nextStep: () => null,
  close: false,
}

const UVModal = styled(Modal)<{ step: number }>(({ step, theme }) => css`
  padding: 0;

  ${up('tablet')} {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  ${DialogWrapper} {
    padding: 0;
    background: ${theme.colors.greyLightest};

  }

  ${DialogBody} {
    padding: 0;
    max-width: 760px;
  }

  > div {
    padding: 0;
    background: ${theme.colors.greyLightest};

    ${up('tablet')} {
      background: ${step === 1 ? 'white' : '#f5f5f5'};
    }
  }
`)

const WidthControl = styled.div`
  width: 100%;

  ${up('tablet')} {
    width: 730px;
  }

`

export const UVPrintModal: FC<PersonalisationModal> = ({
  additionalIDs,
  isChildOfModal = false,
  isOpen,
  onHide,
  product,
  updateProduct = false,
}) => {
  const [state, setState] = useState(initState)
  const inputRef = createRef<HTMLInputElement>()
  const cropperRef = createRef<Cropper>()
  const nextStep = useCallback(() => {
    setState(prevState => ({ ...prevState, step: 2 }))
  }, [])

  const setImageState = (newImageState: ImagePrintState | null) => {
    setState(prevState => ({ ...prevState, imageState: newImageState }))
  }

  const handleOnChange = async (event) => {
    event.preventDefault()

    setImageState(null)
    cropperRef?.current?.destroy()
    if (state.step === 1) {
      nextStep()
    }
    let file = first(inputRef?.current?.files)
    if (!file) {
      return
    }
    const { name, type } = file
    if (type && type === 'image/heic') {
      const heic2any = (await import('heic2any')).default
      file = await heic2any({ blob: file as Blob, toType: 'image/jpeg' }) as File
    }
    setImageState({ ready: true, image: file, name, type })

    // eslint-disable-next-line no-param-reassign
    event.target.value = ''
  }

  useEffect(() => {
    if (state === initState && inputRef.current) {
      setState({
        step: 1,
        nextStep,
        imageState: initState.imageState,
        close: false,
      })
    }
  }, [inputRef, nextStep, state])
  const productPersonalization = getPersonalizationsFromProduct(product)
  const personalization = first(getPersonalizationsByType(productPersonalization, 'uv-print'))
  const UVPrintContent = (
    <WidthControl>

      {state.step === 1
        && (
          <UVPrintStepOne
            inputRef={inputRef}
            price={personalization?.price || ''}
          />
        )}
      {state.step === 2 && (
        <UVPrintStepTwo
          additionalIDs={additionalIDs}
          cropperRef={cropperRef}
          inputRef={inputRef}
          onClose={() => {
            if (onHide) {
              onHide()
            }
          }}
          price={personalization?.price || ''}
          product={product}
          state={state}
          updateProduct={updateProduct}
        />
      )}
      <input
        accept=".png,.jpg,.jpeg,.heic"
        onChange={handleOnChange}
        ref={inputRef}
        style={{ display: 'none' }}
        type="file"
      />
    </WidthControl>
  )
  if (isChildOfModal) {
    return UVPrintContent
  }
  return (
    <UVModal
      ID="uv-print"
      isOpen={isOpen}
      maxWidth="760px"
      onHide={() => {
        setState(prevState => ({ ...prevState, step: 1 }))
        if (onHide) {
          onHide()
        }
      }}
      step={state.step}
    >
      {UVPrintContent}
    </UVModal>
  )
}
